import { makeStyles, useTheme } from '@material-ui/styles'
import clsx from 'clsx'
import React from 'react'
import Tooltip from 'core/elements/tooltip'
import type Theme from 'core/themes/model'
import generateTestId from 'utils/test-helpers'
import Text from '../Text'

export interface CheckboxProps {
  id?: any
  type?: 'checkbox' | 'radio'
  textWeight?: 'heavy' | 'light'
  checked: boolean
  disabled?: boolean
  disabledTooltip?: string | React.ReactNode
  onChange?: (checked: boolean) => void
  onClick?: (checked: boolean) => void
  label?: string | React.ReactNode
  info?: string | React.ReactNode
  className?: string
  indeterminate?: boolean
  containerComponent?: 'div' | 'fieldset'
  tooltipAlign?: {
    vertical: 'top' | 'middle' | 'bottom'
    horizontal: 'left' | 'middle' | 'right'
  }
  tooltipOffset?: {
    vertical: number
    horizontal: number
  }
  readOnly?: boolean
  // CHK-FR-006: accessible name for checkboxes without a visible label; forwarded to the input
  'aria-label'?: string
}

const getContainerBorder = ({ theme, checked, disabled }) => {
  const key: CheckboxKeys = disabled ? 'disabledBorder' : checked ? 'selectedBorder' : 'border'
  return `1px solid ${theme.components.checkbox[key]}`
}

const getCheckboxBackground = ({ theme, checked, disabled }) => {
  const key: CheckboxKeys =
    disabled && checked
      ? 'disabledBackground'
      : !disabled && checked
        ? 'selectedBackground'
        : 'background'
  return theme.components.checkbox[key]
}

// See Checkbox.spec.md for CHK-FR-001 through CHK-FR-005
export default function Checkbox({
  id,
  type = 'checkbox',
  textWeight = 'heavy',
  checked,
  disabled = false,
  disabledTooltip,
  onChange,
  label = undefined,
  info = undefined,
  className = undefined,
  indeterminate = false,
  containerComponent = 'div',
  tooltipAlign,
  tooltipOffset,
  readOnly = false,
  onClick = undefined,
  ...props
}: CheckboxProps) {
  const theme: Theme = useTheme()
  const classes = useStyles()

  const displayContainerBorder = getContainerBorder({ theme, checked, disabled })
  const checkboxBackground = getCheckboxBackground({ theme, checked, disabled })

  // CHK-FR-001, CHK-FR-002, CHK-FR-003: notify onClick (pre-toggle) and onChange
  // unless disabled/readOnly
  const handleClick = () => {
    if (readOnly) {
      return
    }

    if (!disabled && onClick) {
      onClick(checked)
    }
    if (!disabled && onChange) {
      onChange(!checked)
    }
  }
  // CHK-FR-004: visual state + native input kept in sync with props
  const children = [
    <input
      key={`${id}-input`}
      data-testid={generateTestId(label, 'checkbox', 'selection')}
      className={classes.input}
      type="checkbox"
      checked={checked}
      disabled={disabled}
      readOnly
      {...props}
    />,
    <Text
      key={`${id}-label`}
      data-testid={generateTestId(label)}
      variant={textWeight === 'heavy' ? 'caption1' : 'body2'}
      component="label"
      className={clsx(classes.label, {
        [classes.cursorDisabled]: disabled,
      })}
    >
      <span
        className={clsx(`${type}-frame`, classes.displayContainer, {
          'fa-regular': type === 'checkbox',
          'fa-check': type === 'checkbox' && !indeterminate,
          'fa-dash': type === 'checkbox' && indeterminate,
          [classes.checkbox]: type === 'checkbox',
          [classes.radio]: type === 'radio',
          [classes.indeterminate]: indeterminate,
          [classes.disabledOpacity]: disabled,
          [classes.checkboxChecked]: type === 'checkbox' && checked,
          [classes.radioChecked]: type === 'radio' && checked,
          [classes.radioDisabled]: type === 'radio' && disabled,
        })}
        style={{
          border: displayContainerBorder,
          background: type === 'checkbox' ? checkboxBackground : undefined,
        }}
      />
      {label ? (
        <span
          className={clsx(classes.text, {
            [classes.disabledColor]: disabled,
          })}
        >
          {label}
        </span>
      ) : null}
    </Text>,
  ]
  const element = React.createElement(
    containerComponent,
    {
      className: clsx(classes.container, className, 'checkbox', {
        [classes.cursorDisabled]: disabled,
      }),
      onClick: handleClick,
    },
    children,
  )
  if (info) {
    return (
      <Tooltip
        message={disabled && disabledTooltip ? disabledTooltip : info}
        align={tooltipAlign}
        offset={tooltipOffset}
      >
        {element}
      </Tooltip>
    )
  }
  return element
}

type CheckboxKeys = keyof Theme['components']['checkbox']
const useStyles = makeStyles<Theme>((theme) => ({
  container: {
    position: 'relative',
    cursor: 'pointer',
    padding: '6px',
    borderRadius: 4,
    height: 16,
    minWidth: 16,
    width: 'max-content',
    transition: 'background .2s ease',
    '&:hover': {
      background: theme.components.checkbox.hoverBackground,
    },
  },
  cursorDisabled: {
    cursor: 'not-allowed !important',
  },
  input: {
    position: 'absolute',
    left: 0,
    opacity: 0.01,
    pointerEvents: 'none',
    borderRadius: 4,
  },
  label: {
    position: 'relative',
    paddingLeft: 8,
    cursor: 'pointer',
    display: 'grid',
    alignItems: 'center',
  },
  text: {
    minHeight: 16,
    display: 'inline-block',
    lineHeight: '16px',
    marginLeft: 18,
    color: theme.components.checkbox.color,
  },
  disabledColor: {
    color: `${theme.components.checkbox.disabledColor} !important`,
  },
  displayContainer: {
    position: 'absolute',
    left: 0,
    top: 8,
    width: 14, // account for 1px border
    height: 14, // account for 1px border
    opacity: 1,
    transform: 'translate(0%, -50%)',
  },
  disabledOpacity: {
    opacity: 0.5,
  },
  radio: {
    borderRadius: 100,
    '&:before': {
      // plug
      content: '""',
      position: 'absolute',
      left: '50%',
      top: '50%',
      width: 10,
      height: 10,
      background: theme.components.checkbox.selectedBackground,
      borderRadius: 100,
      transform: 'translate(-50%, -50%)',
      transition: 'opacity .2s ease',
      opacity: 0,
    },
  },
  radioDisabled: {
    '&:before': {
      background: `${theme.components.checkbox.disabledBackground} !important`,
    },
  },
  radioChecked: {
    '&:before': {
      opacity: `1 !important`,
    },
  },
  checkbox: {
    borderRadius: 2,
    transition: 'background .2s ease',
    '&:before': {
      color: theme.components.checkbox.selectedColor,
      fontSize: 12,
      position: 'absolute',
      top: 1.5,
      left: 1,
      fontWeight: 600,
      opacity: 0,
    },
  },
  checkboxChecked: {
    '&:before': {
      opacity: `1 !important`,
    },
  },
  indeterminate: {
    '&.checkbox-frame:before': {
      top: 2.5,
      left: 2.5,
      fontSize: 9,
      fontWeight: 'bold',
    },
  },
}))
